import { BillingInterval, PaymentStatus } from '@/store/subscriptions/subscriptions.types';

import { isLifetimePlan, isPaidPlan, planName, renewalOf } from './subscription.display';
import type { Cycle, PlanStatus, Vessel } from './subscription.types';
import type { ManagedVesselSubscription } from './useManagedVesselSubscriptions';

/**
 * A real vessel + subscription as the detail header and hero read it: identity, billing
 * cycle, payment state and what the period end brings.
 *
 * Seats are deliberately absent — they come from `useVesselSeats`, which reads the
 * subscription's own counts against the vessel's people, so nothing here has to stand in
 * for them.
 */

const CYCLE: Record<BillingInterval, Cycle> = {
  [BillingInterval.MONTHLY]: 'monthly',
  [BillingInterval.ANNUALLY]: 'yearly',
};

/** The three states the header chip knows; anything else reads as active. */
const DETAIL_STATUS: Partial<Record<PaymentStatus, PlanStatus>> = {
  [PaymentStatus.TRIALING]: 'trial',
  [PaymentStatus.PAST_DUE]: 'past_due',
  [PaymentStatus.UNPAID]: 'past_due',
  [PaymentStatus.PAUSED]: 'past_due',
};

export const toDetailVessel = ({ vessel, subscription }: ManagedVesselSubscription): Vessel => {
  const identity = {
    id: vessel.id,
    name: vessel.name,
    image: vessel.image,
    ownerId: vessel.ownerId,
    plan: planName(subscription),
  };

  /*
   * Harbor, an unpaid checkout and no subscription row at all are all the free plan:
   * nothing billed, nothing renewing. An unpaid checkout does carry a tier, an interval and
   * a period end, but none of it was provisioned, so none of it is read back here.
   */
  if (!isPaidPlan(subscription)) {
    return { ...identity, cycle: 'monthly', status: 'active', renewal: { kind: 'none', at: null } };
  }

  // Never billed and never renewing, whatever the row's payment status says.
  if (isLifetimePlan(subscription)) {
    return {
      ...identity,
      cycle: 'monthly',
      status: 'lifetime',
      renewal: { kind: 'none', at: null },
    };
  }

  return {
    ...identity,
    cycle: subscription.interval ? CYCLE[subscription.interval] : 'monthly',
    status: (subscription.paymentStatus && DETAIL_STATUS[subscription.paymentStatus]) || 'active',
    renewal: renewalOf(subscription),
  };
};
